import { PlanElement, parsePlanBody, formatSeconds, formatWeight } from "./planElements";

/** One past session, as `GET /clients/{clientId}/exercise-history` returns it. */
export interface RecentSessionSets {
  id: string;
  completed_at: string;
  plan_elements: unknown;
  exercises: {
    element_index: number;
    set_index: number;
    status: "completed" | "skipped";
    reps: number | null;
    weight: number | null;
    weight_unit: "lb" | "kg" | "bodyweight" | null;
    duration_seconds: number | null;
    flagged: boolean;
    flag_note: string | null;
  }[];
}

export type PastSet = RecentSessionSets["exercises"][number];

/** What an athlete did on one exercise in one past session. */
export interface PastExercise {
  sessionId: string;
  completedAt: string;
  sets: PastSet[];
  /** What that session asked for, from its own snapshot: a set "fell short" against this, not today's. */
  targetReps: number | null;
  targetSeconds: number | null;
  /** What the athlete said went wrong, if anything did. */
  note: string | null;
}

function sameExercise(a: PlanElement, b: PlanElement): boolean {
  // The library entry is the exercise's identity when both plans used one. Two coaches' "Squats"
  // typed by hand are matched by name, which is the best a hand-typed exercise has.
  if (a.libraryEntryId && b.libraryEntryId) return a.libraryEntryId === b.libraryEntryId;
  return a.name.trim().toLowerCase() === b.name.trim().toLowerCase();
}

/**
 * This exercise's recent history, newest first: one entry per past session that included it.
 *
 * Matched across plans, not by position. A coach's new plan puts the goblet squat second rather than
 * first, and "last time" should still mean the last time she did goblet squats.
 */
export function historyFor(element: PlanElement | undefined, sessions: RecentSessionSets[]): PastExercise[] {
  if (!element) return [];
  const result: PastExercise[] = [];
  for (const session of sessions) {
    const elements = parsePlanBody(session.plan_elements).elements;
    const indexes = elements.flatMap((candidate, index) => (candidate?.name && sameExercise(candidate, element) ? [index] : []));
    if (indexes.length === 0) continue;
    const sets = (session.exercises ?? []).filter((set) => indexes.includes(set.element_index));
    if (sets.length === 0) continue;
    const prescribed = elements[indexes[0]];
    result.push({
      sessionId: session.id,
      completedAt: session.completed_at,
      sets: [...sets].sort((a, b) => a.set_index - b.set_index),
      targetReps: prescribed.reps ?? null,
      targetSeconds: prescribed.durationSeconds ?? null,
      note: sets.find((set) => set.flagged && set.flag_note)?.flag_note ?? null,
    });
  }
  return result;
}

/** "8 × 15 lb", "8 reps", "30s", or "skipped". */
export function describePastSet(set: PastSet): string {
  if (set.status === "skipped") return "skipped";
  if (set.duration_seconds !== null && set.duration_seconds !== undefined && set.reps === null) {
    return formatSeconds(set.duration_seconds);
  }
  const reps = set.reps !== null && set.reps !== undefined ? set.reps : null;
  const weighed = set.weight !== null && set.weight !== undefined && set.weight_unit !== "bodyweight";
  if (reps !== null && weighed) return `${reps} × ${formatWeight(set.weight!)} ${set.weight_unit ?? ""}`.trim();
  if (reps !== null) return `${reps} reps`;
  if (weighed) return `${formatWeight(set.weight!)} ${set.weight_unit ?? ""}`.trim();
  return "done";
}

/** Did this set come in under what that session asked for? Shown, not judged: a dashed outline. */
export function fellShort(set: PastSet, past: Pick<PastExercise, "targetReps" | "targetSeconds">): boolean {
  if (set.status === "skipped") return true;
  if (past.targetReps !== null && set.reps !== null && set.reps < past.targetReps) return true;
  if (past.targetSeconds !== null && set.duration_seconds !== null && set.duration_seconds < past.targetSeconds) return true;
  return false;
}

/**
 * The one number per session the history chart plots: weight used if this exercise is ever done with
 * weight, otherwise reps, otherwise seconds held. The heaviest or largest set of the session, since
 * that's what the bar is comparing between sessions.
 */
export function chartSeries(history: PastExercise[]): { label: string; points: { sessionId: string; completedAt: string; value: number }[] } | null {
  const done = (past: PastExercise) => past.sets.filter((set) => set.status === "completed");
  const pick = (field: "weight" | "reps" | "duration_seconds") =>
    history
      .map((past) => {
        const values = done(past)
          .filter((set) => field !== "weight" || set.weight_unit !== "bodyweight")
          .map((set) => set[field])
          .filter((value): value is number => typeof value === "number");
        return values.length > 0 ? { sessionId: past.sessionId, completedAt: past.completedAt, value: Math.max(...values) } : null;
      })
      .filter((point): point is { sessionId: string; completedAt: string; value: number } => point !== null);

  const weights = pick("weight");
  if (weights.length > 0) {
    const unit = history.flatMap((past) => past.sets).find((set) => set.weight !== null && set.weight_unit !== "bodyweight")?.weight_unit;
    return { label: `Weight used${unit ? ` (${unit})` : ""}`, points: weights };
  }
  const reps = pick("reps");
  if (reps.length > 0) return { label: "Reps", points: reps };
  const seconds = pick("duration_seconds");
  if (seconds.length > 0) return { label: "Seconds", points: seconds };
  return null;
}
